import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { HubConnection } from '@microsoft/signalr';
import type {
  Doctor,
  TeleNormalSupportQueueItem,
  TelePaymentFollowUpItem,
  TeleStaffChatMessage,
} from '../../types/api';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  buildTeleconsultStaffChatHubConnection,
  canonicalChatUlid,
  getTeleconsultStaffChatHubUrl,
} from '../../realtime/teleconsultStaffChatHub';
import { renderChatMessageLinks } from '../../utils/renderChatMessageLinks';
import { formatDateTimeSG, formatTimeSG, parseApiUtcDate } from '../../utils/date';
import { appendChatMessage, chatHeadInitials, parseChatMessages, staffChatSenderFields, sameMessageList } from './staffChatMessages';
import { StaffMessengerThreadSwitcher } from './StaffMessengerHub';

const QUEUE_POLL_MS = 10_000;
const CHAT_POLL_MS = 2800;
const MAX_OPEN = 6;

type OpenSupportChat = {
  chatUlid: string;
  item: TeleNormalSupportQueueItem;
  messages: TeleStaffChatMessage[];
  draft: string;
  expanded: boolean;
  chatError: string | null;
  sending: boolean;
};

type StaffNormalSupportContextValue = {
  staff: Doctor | null;
  staffId: number | undefined;
  items: TeleNormalSupportQueueItem[];
  followUps: TelePaymentFollowUpItem[];
  filteredFollowUps: TelePaymentFollowUpItem[];
  startingFollowUpId: string | null;
  startFollowUp: (item: TelePaymentFollowUpItem) => Promise<void>;
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (v: string) => void;
  claimingId: string | null;
  releasingId: string | null;
  openChats: OpenSupportChat[];
  setOpenChats: React.Dispatch<React.SetStateAction<OpenSupportChat[]>>;
  loadQueue: (silent?: boolean) => Promise<void>;
  claim: (item: TeleNormalSupportQueueItem) => Promise<void>;
  release: (chatUlid: string) => Promise<void>;
  closeChat: (chatUlid: string) => Promise<void>;
  openChat: (item: TeleNormalSupportQueueItem) => void;
  send: (chatUlid: string) => Promise<void>;
  filtered: TeleNormalSupportQueueItem[];
  mine: TeleNormalSupportQueueItem[];
  unclaimed: TeleNormalSupportQueueItem[];
  others: TeleNormalSupportQueueItem[];
  closed: TeleNormalSupportQueueItem[];
  activeCount: number;
  unhandledCount: number;
};

const StaffNormalSupportContext = createContext<StaffNormalSupportContextValue | null>(null);

export function useStaffNormalSupport(): StaffNormalSupportContextValue {
  const ctx = useContext(StaffNormalSupportContext);
  if (!ctx) {
    throw new Error('StaffNormalSupport components must be used within StaffNormalSupportProvider');
  }
  return ctx;
}

function StaffSupportMessagesPanel({
  chatUlid,
  messages,
}: {
  chatUlid: string;
  messages: TeleStaffChatMessage[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [imageError, setImageError] = useState<Record<string, string>>({});
  const attemptedImagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    run();
    const id = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => cancelAnimationFrame(id);
  }, [messages, imageUrls]);

  const attachmentUlids = useMemo(
    () =>
      messages
        .filter((m) => m.imageUlid && (m.sender === 'patient' || m.sender === 'partner'))
        .map((m) => m.imageUlid as string),
    [messages],
  );

  useEffect(() => {
    if (!chatUlid.trim()) return;
    for (const imageUlid of attachmentUlids) {
      if (attemptedImagesRef.current.has(imageUlid)) continue;
      attemptedImagesRef.current.add(imageUlid);
      void apiService
        .getTeleconsultNormalSupportChatImage(chatUlid, imageUlid)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setImageUrls((prev) => (prev[imageUlid] ? prev : { ...prev, [imageUlid]: url }));
        })
        .catch(() => {
          setImageError((prev) => ({ ...prev, [imageUlid]: 'Could not load attachment' }));
        });
    }
  }, [attachmentUlids, chatUlid]);

  const imageUrlsRef = useRef(imageUrls);
  imageUrlsRef.current = imageUrls;
  useEffect(() => {
    return () => {
      for (const url of Object.values(imageUrlsRef.current)) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  return (
    <div className="staff-messenger-window__messages" ref={scrollRef}>
      {messages.length === 0 ? (
        <p className="staff-messenger-window__empty">No messages yet.</p>
      ) : (
        messages.map((m, idx) => (
          <div
            key={`${m.ts}-${idx}`}
            className={`staff-msg-bubble ${
              m.sender === 'system'
                ? 'staff-msg-bubble--system'
                : m.sender === 'staff'
                  ? 'staff-msg-bubble--staff'
                  : 'staff-msg-bubble--patient'
            }`}
          >
            <span className="staff-msg-bubble__who">
              {m.sender === 'staff' ? 'You' : m.sender === 'system' ? 'System' : m.sender === 'partner' ? 'Partner' : 'Visitor'}
            </span>
            <span className="staff-msg-bubble__body">
              {m.imageUlid ? (
                <div className="staff-msg-bubble__attachment">
                  {!imageUrls[m.imageUlid] && !imageError[m.imageUlid] ? (
                    <span className="staff-msg-bubble__attachment-loading">Loading attachment…</span>
                  ) : imageUrls[m.imageUlid] ? (
                    (m.imageMimeType ?? '').includes('pdf') ? (
                      <a href={imageUrls[m.imageUlid]} target="_blank" rel="noopener noreferrer">
                        View PDF attachment
                      </a>
                    ) : (
                      <img
                        src={imageUrls[m.imageUlid]}
                        alt="Visitor attachment"
                        className="staff-msg-bubble__image"
                      />
                    )
                  ) : (
                    <span className="staff-msg-bubble__attachment-error">
                      {imageError[m.imageUlid] ?? 'Attachment unavailable'}
                    </span>
                  )}
                  {imageUrls[m.imageUlid] ? (
                    <a
                      href={imageUrls[m.imageUlid]}
                      download={m.imageFileName ?? 'attachment'}
                      className="staff-msg-bubble__download"
                    >
                      Download
                    </a>
                  ) : null}
                </div>
              ) : null}
              {m.text.trim() &&
              !(m.imageUlid && (m.text === 'Photo' || m.text === 'PDF'))
                ? renderChatMessageLinks(m.text)
                : null}
            </span>
            <span className="staff-msg-bubble__time">
              {formatTimeSG(m.ts)}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/** Active enquiries with no staff incharge (tab badge). */
export function countUnhandledSupportEnquiries(items: TeleNormalSupportQueueItem[]): number {
  return items.filter((i) => !i.isClosed && (!i.inchargeEmpId || i.inchargeEmpId === 0)).length;
}

/** Tab metrics from provider context (safe fallback when outside provider). */
export function useStaffNormalSupportTabMetrics(): { activeCount: number; unhandledCount: number } {
  const ctx = useContext(StaffNormalSupportContext);
  if (!ctx) {
    return { activeCount: 0, unhandledCount: 0 };
  }
  return { activeCount: ctx.activeCount, unhandledCount: ctx.unhandledCount };
}

/** Polls queue for tab badge — unhandled count only (social-style notification). */
export function useUnhandledSupportEnquiryCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const items = await apiService.getTeleconsultNormalSupportQueue();
        setCount(countUnhandledSupportEnquiries(items));
      } catch {
        /* ignore */
      }
    };

    void load();
    const id = window.setInterval(() => void load(), QUEUE_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return count;
}

export function StaffNormalSupportProvider({
  staff,
  children,
}: {
  staff: Doctor | null;
  children: ReactNode;
}) {
  const [items, setItems] = useState<TeleNormalSupportQueueItem[]>([]);
  const [followUps, setFollowUps] = useState<TelePaymentFollowUpItem[]>([]);
  const [startingFollowUpId, setStartingFollowUpId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [openChats, setOpenChats] = useState<OpenSupportChat[]>([]);
  const openChatsRef = useRef(openChats);
  openChatsRef.current = openChats;
  const hubRef = useRef<HubConnection | null>(null);
  const staffId = staff?.id;

  const loadQueue = useCallback(async (silent?: boolean) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const [rows, paymentFollowUps] = await Promise.all([
        apiService.getTeleconsultNormalSupportQueue(),
        apiService.getTeleconsultPaymentFollowUp(),
      ]);
      setItems(rows);
      setFollowUps(paymentFollowUps);
      setOpenChats((prev) =>
        prev.map((c) => {
          const updated = rows.find((r) => r.chatUlid === c.chatUlid);
          return updated ? { ...c, item: updated } : c;
        }),
      );
    } catch (err: unknown) {
      if (!silent) {
        setError(getApiErrorMessage(err, 'Could not load support enquiries. Please try again.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
    const id = window.setInterval(() => void loadQueue(true), QUEUE_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadQueue]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.visitorName.toLowerCase().includes(q) ||
        i.visitorPhone.toLowerCase().includes(q) ||
        (i.displaySubtitle ?? '').toLowerCase().includes(q) ||
        (i.corporateCompanyCode ?? '').toLowerCase().includes(q) ||
        (i.lastMessagePreview ?? '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const filteredFollowUps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return followUps;
    return followUps.filter(
      (i) =>
        i.patientName.toLowerCase().includes(q) ||
        i.phone.toLowerCase().includes(q) ||
        (i.email ?? '').toLowerCase().includes(q),
    );
  }, [followUps, search]);

  const active = useMemo(() => filtered.filter((i) => !i.isClosed), [filtered]);
  const closedItems = useMemo(
    () =>
      [...filtered.filter((i) => i.isClosed)].sort(
        (a, b) =>
          new Date(parseApiUtcDate(b.dateClosed ?? b.dateCreated)).getTime() -
          new Date(parseApiUtcDate(a.dateClosed ?? a.dateCreated)).getTime(),
      ),
    [filtered],
  );

  const mine = staffId ? active.filter((i) => i.inchargeEmpId === staffId) : [];
  const unclaimed = active.filter((i) => !i.inchargeEmpId);
  const others = staffId
    ? active.filter((i) => i.inchargeEmpId && i.inchargeEmpId !== staffId)
    : active.filter((i) => !!i.inchargeEmpId);

  const openChatIdsKey = useMemo(
    () => openChats.map((c) => c.chatUlid).sort().join('|'),
    [openChats],
  );

  useEffect(() => {
    const ids = openChatIdsKey ? openChatIdsKey.split('|').filter(Boolean) : [];
    if (ids.length === 0) {
      void hubRef.current?.stop();
      hubRef.current = null;
      return;
    }

    const hubUrl = getTeleconsultStaffChatHubUrl();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const conn = buildTeleconsultStaffChatHubConnection({
      hubUrl,
      accessToken: token,
      onStaffChatUpdated: (payload) => {
        const ulid = canonicalChatUlid(payload.chatUlid);
        if (!ulid || typeof payload.chatConversation !== 'string') return;
        const messages = parseChatMessages(payload.chatConversation);
        setOpenChats((prev) =>
          prev.map((c) => {
            if (canonicalChatUlid(c.chatUlid) !== ulid) return c;
            if (c.sending) return c;
            if (sameMessageList(c.messages, messages)) return c;
            return { ...c, messages, chatError: null };
          }),
        );
      },
    });

    const joinAll = async () => {
      for (const id of ids) {
        try {
          await conn.invoke('JoinConversation', id);
        } catch {
          /* negotiating */
        }
      }
    };

    hubRef.current = conn;
    let cancelled = false;
    void (async () => {
      try {
        await conn.start();
        if (!cancelled) await joinAll();
      } catch (e) {
        console.warn('[SignalR normal support]', e);
      }
    })();

    return () => {
      cancelled = true;
      void conn.stop();
      if (hubRef.current === conn) hubRef.current = null;
    };
  }, [openChatIdsKey]);

  useEffect(() => {
    let intervalId: number | undefined;
    let inFlight = false;
    const poll = async () => {
      if (inFlight || openChatsRef.current.length === 0) return;
      inFlight = true;
      try {
        const pairs = await Promise.all(
          openChatsRef.current.map(async (c) => {
            try {
              const res = await apiService.getTeleconsultNormalSupportChat(c.chatUlid);
              return { chatUlid: c.chatUlid, messages: parseChatMessages(res.chatConversation) };
            } catch {
              return { chatUlid: c.chatUlid, messages: null as TeleStaffChatMessage[] | null };
            }
          }),
        );
        setOpenChats((prev) =>
          prev.map((c) => {
            const p = pairs.find((x) => x.chatUlid === c.chatUlid);
            if (!p || p.messages === null || c.sending) return c;
            if (sameMessageList(c.messages, p.messages)) return c;
            return { ...c, messages: p.messages };
          }),
        );
      } finally {
        inFlight = false;
      }
    };
    void poll();
    intervalId = window.setInterval(() => void poll(), CHAT_POLL_MS);
    return () => {
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [openChats.length]);

  const claim = async (item: TeleNormalSupportQueueItem) => {
    setClaimingId(item.chatUlid);
    try {
      await apiService.claimTeleconsultNormalSupportChat(item.chatUlid);
      await loadQueue(true);
      openChat(staffId ? { ...item, inchargeEmpId: staffId } : item);
    } catch (e: unknown) {
      alert(getApiErrorMessage(e, 'Could not claim this chat. Please try again.'));
    } finally {
      setClaimingId(null);
    }
  };

  const release = async (chatUlid: string) => {
    setReleasingId(chatUlid);
    try {
      await apiService.releaseTeleconsultNormalSupportChat(chatUlid);
      await loadQueue(true);
    } catch (e: unknown) {
      alert(getApiErrorMessage(e, 'Could not release this chat. Please try again.'));
    } finally {
      setReleasingId(null);
    }
  };

  const openChat = (item: TeleNormalSupportQueueItem) => {
    if (openChats.length >= MAX_OPEN) {
      alert(`Maximum ${MAX_OPEN} support chats open.`);
      return;
    }
    setOpenChats((prev) => {
      const compact =
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
      if (prev.some((c) => c.chatUlid === item.chatUlid)) {
        return prev.map((c) =>
          c.chatUlid === item.chatUlid
            ? { ...c, expanded: true, item }
            : compact
              ? { ...c, expanded: false }
              : c,
        );
      }
      return [
        ...(compact ? prev.map((c) => ({ ...c, expanded: false })) : prev),
        {
          chatUlid: item.chatUlid,
          item,
          messages: [],
          draft: '',
          expanded: true,
          chatError: null,
          sending: false,
        },
      ];
    });
    void (async () => {
      try {
        const res = await apiService.getTeleconsultNormalSupportChat(item.chatUlid);
        const messages = parseChatMessages(res.chatConversation);
        setOpenChats((prev) =>
          prev.map((c) => (c.chatUlid === item.chatUlid ? { ...c, messages } : c)),
        );
      } catch {
        /* poll will retry */
      }
    })();
  };

  const startFollowUp = async (item: TelePaymentFollowUpItem) => {
    setStartingFollowUpId(item.visitUlid);
    try {
      const chat = await apiService.startTeleconsultPaymentFollowUpChat(item.visitUlid);
      await loadQueue(true);
      openChat({
        ...chat,
        visitorEmail: chat.visitorEmail || item.email || null,
        inchargeEmpId: staffId ?? chat.inchargeEmpId,
      });
    } catch (e: unknown) {
      alert(getApiErrorMessage(e, 'Could not start this chat. Please try again.'));
    } finally {
      setStartingFollowUpId(null);
    }
  };

  const send = async (chatUlid: string) => {
    const chat = openChatsRef.current.find((c) => c.chatUlid === chatUlid);
    if (!chat || chat.sending || !chat.draft.trim()) return;
    const text = chat.draft.replace(/\r\n/g, '\n').trim();
    const msg: TeleStaffChatMessage = { sender: 'staff', text, ts: Date.now(), ...staffChatSenderFields(staff) };
    const optimistic = [...chat.messages, msg];
    setOpenChats((prev) =>
      prev.map((c) =>
        c.chatUlid === chatUlid ? { ...c, messages: optimistic, draft: '', sending: true, chatError: null } : c,
      ),
    );
    try {
      // Merge against server so visitor attachments are not dropped by a concurrent upload.
      const server = await apiService.getTeleconsultNormalSupportChat(chatUlid);
      const next = appendChatMessage(parseChatMessages(server.chatConversation), msg);
      await apiService.saveTeleconsultNormalSupportChat(chatUlid, next, staff?.id ?? null);
      setOpenChats((prev) =>
        prev.map((c) => (c.chatUlid === chatUlid ? { ...c, messages: next, sending: false } : c)),
      );
      await loadQueue(true);
    } catch (e: unknown) {
      setOpenChats((prev) =>
        prev.map((c) =>
          c.chatUlid === chatUlid
            ? {
                ...c,
                sending: false,
                chatError: e instanceof Error ? e.message : 'Send failed',
              }
            : c,
        ),
      );
    }
  };

  const closeChat = async (chatUlid: string) => {
    if (
      !window.confirm(
        'End this support chat? The visitor will see a closing message and it will leave the active queue.',
      )
    ) {
      return;
    }
    try {
      await apiService.closeTeleconsultNormalSupportChat(chatUlid);
      setOpenChats((prev) => prev.filter((c) => c.chatUlid !== chatUlid));
      await loadQueue(true);
    } catch (e: unknown) {
      alert(getApiErrorMessage(e, 'Could not end this chat. Please try again.'));
    }
  };

  const value: StaffNormalSupportContextValue = {
    staff,
    staffId,
    items,
    followUps,
    filteredFollowUps,
    startingFollowUpId,
    startFollowUp,
    loading,
    error,
    search,
    setSearch,
    claimingId,
    releasingId,
    openChats,
    setOpenChats,
    loadQueue,
    claim,
    release,
    closeChat,
    openChat,
    send,
    filtered,
    mine,
    unclaimed,
    others,
    closed: closedItems,
    activeCount:
      active.length +
      filteredFollowUps.filter((f) => {
        if (!f.existingChatUlid) return true;
        return !active.some((row) => row.chatUlid === f.existingChatUlid);
      }).length,
    unhandledCount:
      countUnhandledSupportEnquiries(items) + followUps.filter((f) => !f.existingChatUlid).length,
  };

  return (
    <StaffNormalSupportContext.Provider value={value}>{children}</StaffNormalSupportContext.Provider>
  );
}

export function StaffNormalSupportQueue() {
  const {
    loading,
    error,
    search,
    setSearch,
    loadQueue,
    items,
    followUps,
    filteredFollowUps,
    startingFollowUpId,
    startFollowUp,
    unclaimed,
    mine,
    others,
    closed,
    claimingId,
    openChats,
    claim,
    openChat,
    staffId,
  } = useStaffNormalSupport();

  const renderCard = (item: TeleNormalSupportQueueItem, options?: { closedSection?: boolean }) => {
    const mineRow = staffId && item.inchargeEmpId === staffId;
    const unclaimedRow = !item.inchargeEmpId;
    const isClosedRow = item.isClosed === true;
    const canOpen = isClosedRow ? true : mineRow;
    const isOpen = openChats.some((c) => c.chatUlid === item.chatUlid);

    return (
      <div
        key={item.chatUlid}
        className={`staff-tele-card staff-normal-support-card${isClosedRow ? ' staff-normal-support-card--closed' : ''}`}
      >
        <div className="staff-tele-card__main staff-normal-support-card__main" style={{ padding: '12px 14px' }}>
          <div className="staff-normal-support-card__top">
            {isClosedRow ? (
              <span className="staff-normal-support-badge staff-normal-support-badge--closed">Closed</span>
            ) : item.isClosingPending ? (
              <span className="staff-normal-support-badge staff-normal-support-badge--closing">Closing</span>
            ) : item.awaitingStaffReply ? (
              <span className="staff-normal-support-badge staff-normal-support-badge--new">New reply</span>
            ) : unclaimedRow ? (
              <span className="staff-normal-support-badge staff-normal-support-badge--incoming">Incoming</span>
            ) : null}
            <div className="staff-normal-support-card__identity">
              <div className="patient-name">
                {item.visitorName || 'Visitor'}
                {item.isCorporatePartner && (
                  <span className="staff-normal-support-badge staff-normal-support-badge--corporate">
                    Corporate
                  </span>
                )}
              </div>
              <div className="patient-nric">{item.displaySubtitle || item.visitorPhone}</div>
              {item.visitorEmail ? <div className="patient-nric">{item.visitorEmail}</div> : null}
              <div className="staff-payment-followup__when">
                Started {formatDateTimeSG(item.dateCreated)}
                {item.dateClosed ? (
                  <>
                    <br />
                    Closed {formatDateTimeSG(item.dateClosed)}
                  </>
                ) : null}
              </div>
            </div>
          </div>
          {item.lastMessagePreview ? (
            <p style={{ fontSize: 12, color: '#475569', marginTop: 10, marginBottom: 0 }}>
              {item.lastMessagePreview}
            </p>
          ) : null}
          {item.inchargeEmpCode ? (
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
              Incharge: <strong>{item.inchargeEmpCode}</strong>
            </p>
          ) : null}
        </div>
        <div className="staff-tele-card__actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 12px' }}>
          {!options?.closedSection && !isClosedRow ? (
            <button
              type="button"
              className="staff-tele-btn staff-tele-btn--pick"
              disabled={(!!item.inchargeEmpId && !mineRow) || claimingId === item.chatUlid}
              onClick={() => void claim(item)}
            >
              {mineRow ? 'Claimed · yours' : claimingId === item.chatUlid ? 'Claiming…' : 'Pick for chat'}
            </button>
          ) : null}
          <button
            type="button"
            className="staff-tele-btn staff-tele-btn--openchat"
            disabled={!canOpen}
            title={!canOpen ? 'Pick this enquiry first' : undefined}
            onClick={() => openChat(item)}
          >
            {isOpen
              ? 'Focus chat'
              : isClosedRow
                ? 'View history'
                : 'Open chat'}
          </button>
        </div>
      </div>
    );
  };

  const phoneCanChat = (phone: string) => (phone.match(/\d/g) ?? []).length >= 6;
  const awaitingPayment = filteredFollowUps.filter((item) => item.status !== 'paymentTimedOut');
  const paymentTimedOut = filteredFollowUps.filter((item) => item.status === 'paymentTimedOut');
  const searching = search.trim().length > 0;

  const renderFollowUp = (item: TelePaymentFollowUpItem) => {
    const timedOut = item.status === 'paymentTimedOut';
    const mineRow = !!staffId && item.chatInchargeEmpId === staffId;
    const blocked = !!item.chatInchargeEmpId && !mineRow;
    const canChat = !blocked && (phoneCanChat(item.phone) || !!item.existingChatUlid);
    const buttonLabel = startingFollowUpId === item.visitUlid
      ? 'Starting…'
      : blocked
        ? `Handled by ${item.chatInchargeEmpCode || 'a colleague'}`
        : item.existingChatUlid
          ? mineRow
            ? 'Open chat'
            : 'Pick up chat'
          : 'Start chat';

    return (
      <div key={item.visitUlid} className="staff-tele-card staff-normal-support-card">
        <div className="staff-tele-card__main staff-normal-support-card__main" style={{ padding: '12px 14px' }}>
          <div className="staff-normal-support-card__top">
            <span
              className={`staff-normal-support-badge ${
                item.paymentReceivedAfterCancel
                  ? 'staff-normal-support-badge--latepay'
                  : timedOut
                    ? 'staff-normal-support-badge--timeout'
                    : 'staff-normal-support-badge--payment'
              }`}
            >
              {item.paymentReceivedAfterCancel ? 'Paid after cancel' : timedOut ? 'Payment timed out' : 'Awaiting payment'}
            </span>
            <div className="staff-normal-support-card__identity">
              <div className="patient-name">{item.patientName || 'Patient'}</div>
              <div className="staff-payment-followup__contact">
                {item.phone ? <a href={`tel:${item.phone}`}>{item.phone}</a> : <span>No phone on file</span>}
                <br />
                {item.email ? <a href={`mailto:${item.email}`}>{item.email}</a> : <span>No email on file</span>}
              </div>
              <div className="staff-payment-followup__when">
                {item.registeredAt ? <>Registered {formatDateTimeSG(item.registeredAt)}</> : 'Registered'}
                {timedOut && item.cancelledAt ? (
                  <>
                    <br />
                    Cancelled {formatDateTimeSG(item.cancelledAt)}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="staff-tele-card__actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 12px' }}>
          <button
            type="button"
            className="staff-tele-btn staff-tele-btn--pick"
            disabled={!canChat || startingFollowUpId === item.visitUlid}
            title={
              blocked
                ? 'Another staff member already has this chat'
                : !phoneCanChat(item.phone) && !item.existingChatUlid
                  ? 'No phone number on file. Contact the patient by email.'
                  : undefined
            }
            onClick={() => void startFollowUp(item)}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <p className="staff-tele-intro">
        Live enquiries, plus patients who registered and have not paid. <strong>Start chat</strong> reaches
        someone still on the payment page. If payment was not completed in time, their name, phone, and email
        stay here so you can follow up. Open chats stay in the bottom dock when you switch tabs.
      </p>

      <div className="filter-bar">
        <input
          type="search"
          placeholder="Search name, phone, email, or message…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search support enquiries"
        />
        <button type="button" className="btn btn-secondary" onClick={() => void loadQueue()}>
          Refresh
        </button>
      </div>

      {error ? <div className="staff-tele-alert staff-tele-alert--error">{error}</div> : null}

      {loading ? (
        <div className="staff-tele-loading">Loading support enquiries…</div>
      ) : items.length === 0 && followUps.length === 0 ? (
        <p className="staff-queue-duo__empty">
          No support work yet. Visitors appear when they start a live chat. Patients who register without paying,
          and visits auto-cancelled for unpaid payment, appear here as well.
        </p>
      ) : unclaimed.length === 0 &&
        mine.length === 0 &&
        others.length === 0 &&
        closed.length === 0 &&
        filteredFollowUps.length === 0 ? (
        <p className="staff-queue-duo__empty">No enquiries match your search.</p>
      ) : (
        <div className="staff-normal-support-sections">
          {!searching || awaitingPayment.length > 0 ? (
            <section className="staff-queue-subsection">
              <header className="staff-queue-subsection__head">
                <h3 className="staff-queue-subsection__title">Registered, not paid</h3>
                <span className="staff-queue-subsection__count">{awaitingPayment.length}</span>
              </header>
              <p className="staff-queue-subsection__lead">
                On the patient portal with payment still outstanding. Start a chat if they may be stuck.
              </p>
              <div className="staff-queue-duo__grid">
                {awaitingPayment.length === 0 ? (
                  <p className="staff-queue-duo__empty">No one is waiting on payment.</p>
                ) : (
                  awaitingPayment.map((item) => renderFollowUp(item))
                )}
              </div>
            </section>
          ) : null}

          {!searching || paymentTimedOut.length > 0 ? (
            <section className="staff-queue-subsection">
              <header className="staff-queue-subsection__head">
                <h3 className="staff-queue-subsection__title">Payment not completed</h3>
                <span className="staff-queue-subsection__count">{paymentTimedOut.length}</span>
              </header>
              <p className="staff-queue-subsection__lead">
                Auto-cancelled because payment was not completed in time. Use the phone or email to follow up.
              </p>
              <div className="staff-queue-duo__grid">
                {paymentTimedOut.length === 0 ? (
                  <p className="staff-queue-duo__empty">No unpaid auto-cancellations today.</p>
                ) : (
                  paymentTimedOut.map((item) => renderFollowUp(item))
                )}
              </div>
            </section>
          ) : null}

          <section className="staff-queue-subsection">
            <header className="staff-queue-subsection__head">
              <h3 className="staff-queue-subsection__title">Needs attention</h3>
              <span className="staff-queue-subsection__count">{unclaimed.length}</span>
            </header>
            <p className="staff-queue-subsection__lead">Unclaimed enquiries or waiting for a staff reply.</p>
            <div className="staff-queue-duo__grid">
              {unclaimed.length === 0 ? (
                <p className="staff-queue-duo__empty">No unclaimed enquiries.</p>
              ) : (
                unclaimed.map((item) => renderCard(item))
              )}
            </div>
          </section>

          <section className="staff-queue-subsection">
            <header className="staff-queue-subsection__head">
              <h3 className="staff-queue-subsection__title">Your enquiries</h3>
              <span className="staff-queue-subsection__count">{mine.length}</span>
            </header>
            <div className="staff-queue-duo__grid">
              {mine.length === 0 ? (
                <p className="staff-queue-duo__empty">Pick an enquiry from the list above.</p>
              ) : (
                mine.map((item) => renderCard(item))
              )}
            </div>
          </section>

          {others.length > 0 ? (
            <section className="staff-queue-subsection">
              <header className="staff-queue-subsection__head">
                <h3 className="staff-queue-subsection__title">Handled by colleagues</h3>
                <span className="staff-queue-subsection__count">{others.length}</span>
              </header>
              <div className="staff-queue-duo__grid">{others.map((item) => renderCard(item))}</div>
            </section>
          ) : null}

          <section className="staff-queue-subsection staff-queue-subsection--closed">
            <header className="staff-queue-subsection__head">
              <h3 className="staff-queue-subsection__title">Closed</h3>
              <span className="staff-queue-subsection__count">{closed.length}</span>
            </header>
            <p className="staff-queue-subsection__lead">
              Ended support chats (read-only). Most recent 200 shown.
            </p>
            <div className="staff-queue-duo__grid">
              {closed.length === 0 ? (
                <p className="staff-queue-duo__empty">No closed support chats yet.</p>
              ) : (
                closed.map((item) => renderCard(item, { closedSection: true }))
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

/** General support messenger windows — always mounted so chats persist across tabs. */
export function StaffNormalSupportDock() {
  const { staffId, openChats, setOpenChats, releasingId, release, closeChat, send } = useStaffNormalSupport();

  if (openChats.length === 0) {
    return null;
  }

  return (
    <div className="staff-messenger-dock staff-messenger-dock--support" aria-label="General support conversations">
      {openChats.map((c, stackIndex) => {
        const mineChat = staffId && c.item.inchargeEmpId === staffId;
        const readOnly = c.item.isClosed === true;
        const canReply = mineChat && !readOnly;
        return (
          <div
            key={c.chatUlid}
            className={`staff-messenger-slot ${c.expanded ? 'staff-messenger-slot--expanded' : ''}`}
            style={{ zIndex: 90 + stackIndex }}
          >
            {c.expanded ? (
              <div className="staff-messenger-window">
                <div className="staff-messenger-window__titlebar">
                  <div className="staff-messenger-window__title">
                    <strong>{c.item.visitorName || 'Visitor'}</strong>
                    <span className="staff-messenger-window__sub">{c.item.displaySubtitle || c.item.visitorPhone}</span>
                  </div>
                  {mineChat && !readOnly ? (
                    <div className="staff-messenger-window__actions">
                      <button
                        type="button"
                        className="staff-messenger-textbtn"
                        disabled={releasingId === c.chatUlid}
                        onClick={() => void release(c.chatUlid)}
                      >
                        {releasingId === c.chatUlid ? 'Releasing…' : 'Release'}
                      </button>
                      <button
                        type="button"
                        className="staff-messenger-textbtn staff-messenger-textbtn--end"
                        onClick={() => void closeChat(c.chatUlid)}
                      >
                        End chat
                      </button>
                    </div>
                  ) : null}
                  <div className="staff-messenger-window__chrome">
                    <button
                      type="button"
                      className="staff-messenger-iconbtn"
                      onClick={() =>
                        setOpenChats((prev) =>
                          prev.map((x) =>
                            x.chatUlid === c.chatUlid ? { ...x, expanded: false } : x,
                          ),
                        )
                      }
                    >
                      ─
                    </button>
                    <button
                      type="button"
                      className="staff-messenger-iconbtn"
                      onClick={() =>
                        setOpenChats((prev) => prev.filter((x) => x.chatUlid !== c.chatUlid))
                      }
                    >
                      ×
                    </button>
                  </div>
                  <StaffMessengerThreadSwitcher />
                </div>
                {readOnly ? (
                  <div className="staff-messenger-window__banner staff-messenger-window__banner--info">
                    This chat is closed — history is read-only.
                  </div>
                ) : null}
                {c.chatError ? <div className="staff-messenger-window__banner">{c.chatError}</div> : null}
                <StaffSupportMessagesPanel chatUlid={c.chatUlid} messages={c.messages} />
                {!readOnly ? (
                <form
                  className="staff-messenger-window__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send(c.chatUlid);
                  }}
                >
                  <div className="staff-messenger-compose">
                    <textarea
                      className="staff-messenger-compose__field"
                      value={c.draft}
                      onChange={(e) =>
                        setOpenChats((prev) =>
                          prev.map((x) =>
                            x.chatUlid === c.chatUlid ? { ...x, draft: e.target.value } : x,
                          ),
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (canReply && !c.sending && c.draft.trim()) {
                            void send(c.chatUlid);
                          }
                        }
                      }}
                      placeholder={
                        c.item.isClosingPending
                          ? 'Closing — visitor can still reply…'
                          : 'Reply to visitor…'
                      }
                      rows={3}
                      disabled={!canReply}
                      aria-busy={c.sending}
                      autoComplete="off"
                    />
                    <div className="staff-messenger-compose__actions">
                      <button
                        type="submit"
                        className="staff-messenger-compose__send"
                        disabled={!canReply || c.sending || !c.draft.trim()}
                        aria-label={c.sending ? 'Sending…' : 'Send message'}
                        title="Send message"
                      >
                        {c.sending ? (
                          <span className="staff-messenger-compose__sending" aria-hidden />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="staff-messenger-compose__hint">Enter to send · Shift+Enter for a new line</p>
                </form>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                className="staff-messenger-pill staff-messenger-pill--support"
                onClick={() =>
                  setOpenChats((prev) => {
                    const compact =
                      typeof window !== 'undefined' &&
                      window.matchMedia('(max-width: 768px)').matches;
                    return prev.map((x) =>
                      x.chatUlid === c.chatUlid
                        ? { ...x, expanded: true }
                        : compact
                          ? { ...x, expanded: false }
                          : x,
                    );
                  })
                }
                title={`Open support chat: ${c.item.visitorName || 'Visitor'}`}
                aria-label={`Open support chat with ${c.item.visitorName || 'Visitor'}`}
              >
                <span className="staff-messenger-pill__avatar" aria-hidden>
                  {chatHeadInitials(c.item.visitorName || 'Visitor')}
                </span>
                <span className="staff-messenger-pill__name">{c.item.visitorName || 'Visitor'}</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** @deprecated Use StaffNormalSupportQueue — kept for imports */
export default function StaffNormalSupportPanel({ staff }: { staff: Doctor | null }) {
  return (
    <StaffNormalSupportProvider staff={staff}>
      <StaffNormalSupportQueue />
      <StaffNormalSupportDock />
    </StaffNormalSupportProvider>
  );
}
